/**
 * Client-side model for the member journey projection served by
 * member-operations-service (`GET /v1/member-operations/members/:patientId/journey`).
 * Types mirror `schemas/memberJourneyProjection.ts` in that service; the
 * projection carries ids and source names, so touchpoint labels and the
 * operator-facing summary (per-stage tallies, default selection, touchpoint
 * status, evidence lines) are derived here.
 */

export type MemberJourneyConfidence =
  | 'confirmed'
  | 'inferred'
  | 'stage_fallback'
  | 'none';

export type MemberJourneyStageId =
  | 'onboarding'
  | 'day_of_diagnostics'
  | 'review_of_results'
  | 'care_period_1'
  | 'care_period_2'
  | 'renewal_period';

export type MemberJourneyEvidence =
  | { kind: 'fhir_appointment'; id: string; status?: string; start: string }
  | { kind: 'fhir_consent'; id: string; dateTime?: string }
  | { kind: 'fhir_questionnaire_response'; id: string; authored?: string }
  | {
      kind: 'salesforce_task';
      id: string;
      subject: string;
      status?: string;
      completedAt?: string;
    }
  | { kind: 'salesforce_stage'; stage: string }
  | {
      kind: 'fhir_composition';
      id: string;
      status?: string;
      startDate?: string;
      received: number;
      expected: number;
      title?: string;
    }
  | {
      kind: 'fhir_diagnostic_report';
      id: string;
      effective?: string;
      provenance: MemberJourneyResultProvenance;
      documentType?: string;
      label?: string;
      resultCount?: number;
    }
  | {
      kind: 'fhir_document_reference';
      id: string;
      created?: string;
      provenance: MemberJourneyResultProvenance;
      documentType?: string;
      label?: string;
    }
  | {
      kind: 'fhir_observation';
      id: string;
      effective?: string;
      provenance: MemberJourneyResultProvenance;
      reviewed: boolean;
      documentType?: string;
      label?: string;
    }
  | {
      kind: 'fhir_procedure_request';
      id: string;
      authoredOn?: string;
      status?: string;
      /** Absent for lab orders. */
      category?: 'imaging';
      /** Ordered test name (imaging orders only). */
      label?: string;
      testCount: number;
      vendor?: 'quest';
      inHouse: boolean;
    };

export type MemberJourneyResultProvenance =
  | 'charted'
  | 'imaging_system'
  | 'extracted';

export type MemberJourneyResultExpectation =
  | 'consent'
  | 'lab_order'
  | 'imaging_order'
  | 'apex_protocol';

export type MemberJourneyTouchpoint = {
  id: string;
  complete: boolean;
  confidence: MemberJourneyConfidence;
  /** Projector's own colour; not used here, classification is `toneFor`. */
  signal?: string;
  occurredAt?: string;
  scheduledAt?: string;
  expectedAt?: string;
  /** Result items only: why the result is expected; empty means not tracked. */
  expectedBy?: MemberJourneyResultExpectation[];
  evidence: MemberJourneyEvidence[];
  items?: MemberJourneyTouchpoint[];
};

export type MemberJourneyQuarantineReason =
  | 'no_anchor'
  | 'outside_window'
  | 'ambiguous'
  | 'unknown_type'
  | 'undated'
  | 'unreviewed_extraction';

export type MemberJourneyResultModality =
  | 'labs'
  | 'mri'
  | 'ccta'
  | 'dexa'
  | 'vo2_max';

/** Results known for the member that the projector could not place. */
export type MemberJourneyQuarantineItem = {
  reason: MemberJourneyQuarantineReason;
  modality?: MemberJourneyResultModality;
  documentType?: string;
  count: number;
  firstAt?: string;
  lastAt?: string;
  evidence: MemberJourneyEvidence[];
};

export type MemberJourneyStage = {
  id: MemberJourneyStageId;
  complete: boolean;
  touchpoints: MemberJourneyTouchpoint[];
};

export type MemberJourneyProjection = {
  generatedAt: string;
  patientId: string;
  membership: {
    salesforceAccountId?: string;
    packageId?: string;
    recordType?: string;
    packageYear?: string;
    designation?: string;
    status?: string;
    accessReason?: string;
    termStart?: string;
    termEnd?: string;
  };
  salesforce: {
    stage?: string;
    lastStageChangeDate?: string;
    diagnosticsDate?: string;
    rorCompletedDate?: string;
    templateTasksPresent: boolean;
  };
  anchors: { dayOfDiagnostics?: string };
  complete: boolean;
  stages: MemberJourneyStage[];
  /** Absent on projections written before diagnostics results shipped. */
  quarantine?: MemberJourneyQuarantineItem[];
};

export const MEMBER_JOURNEY_QUERY_KEY = ['member-journey-projection'] as const;

export const memberJourneyPath = (patientId: string): string =>
  `/v1/member-operations/members/${patientId}/journey`;

export const STAGE_LABELS: Record<MemberJourneyStageId, string> = {
  onboarding: 'Onboarding',
  day_of_diagnostics: 'Day of diagnostics',
  review_of_results: 'Review of results',
  care_period_1: 'Care period 1',
  care_period_2: 'Care period 2',
  renewal_period: 'Renewal period',
};

export const TOUCHPOINT_LABELS: Record<string, string> = {
  dod_scheduled: 'Diagnostics scheduled',
  care_coordinator_meet_and_greet: 'CC meet and greet',
  app_prescreen: 'APP prescreen',
  physician_personalized_consult: 'Physician personalized consult',
  cc_pre_visit_check_in: 'CC pre-visit check-in',
  cc_pre_visit_call: 'CC pre-visit call',
  intake_consents: 'Intake consents',
  intake_surveys: 'Intake surveys',
  day_of_diagnostics: 'Day of diagnostics',
  after_visit_thanks: 'After-visit thanks',
  followup_labs_information: 'Follow-up labs information',
  physician_imaging_overview: 'Physician imaging overview',
  cc_onboard_member_to_flma: 'CC app onboarding',
  physician_initial_ror: 'Physician initial ROR',
  ror_48hr_followup: 'ROR 48hr follow-up',
  nutrition_consultation: 'Nutrition consultation',
  app_check_in_12wk: 'APP 12-week check-in',
  app_lab_orders_1: 'APP lab orders',
  physician_4mo_lab_ror: 'Physician 4mo lab ROR',
  app_4mo_followup: 'APP 4mo follow-up',
  nutrition_followup_1: 'Nutrition follow-up',
  provider_mid_year_progress: 'Mid-year progress',
  app_lab_orders_2: 'APP lab orders',
  cd_mid_year_check_in: 'CD mid-year check-in',
  cc_7mo_check_in: 'CC 7mo check-in',
  physician_8mo_lab_ror: 'Physician 8mo lab ROR',
  app_8mo_followup: 'APP 8mo follow-up',
  physician_annual_review_of_progress: 'Physician annual review',
  nutrition_followup_2: 'Nutrition follow-up',
  cd_renewal_email: 'CD renewal email',
  app_renewal_readiness: 'APP renewal readiness',
  cc_journey_maintenance: 'CC journey maintenance',
  apex_membership_agreement: 'APEX membership agreement',
  phi_authorization_form: 'PHI authorization form',
  mri_consent: 'MRI consent',
  ccta_consent: 'CCTA consent',
  dexa_consent: 'DEXA consent',
  vo2_max_consent: 'VO2 max consent',
  mri_safety_screening: 'MRI safety screening',
  apex_member_intake: 'APEX member intake',
  dod_labs_results: 'Labs results',
  mri_results: 'MRI results',
  ccta_results: 'CCTA results',
  dexa_results: 'DEXA results',
  vo2_max_results: 'VO2 max results',
  labs_4mo_results: '4mo labs results',
  labs_8mo_results: '8mo labs results',
};

export const QUARANTINE_REASON_LABELS: Record<
  MemberJourneyQuarantineReason,
  string
> = {
  no_anchor: 'No day of diagnostics',
  outside_window: 'Outside result windows',
  ambiguous: 'Between two windows',
  unknown_type: 'Unknown document type',
  undated: 'No date',
  unreviewed_extraction: 'Extraction not reviewed',
};

export const RESULT_MODALITY_LABELS: Record<
  MemberJourneyResultModality,
  string
> = {
  labs: 'Labs',
  mri: 'MRI',
  ccta: 'CCTA',
  dexa: 'DEXA',
  vo2_max: 'VO2 max',
};

const humanize = (id: string): string => {
  const spaced = id.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const stageLabel = (id: string): string =>
  STAGE_LABELS[id as MemberJourneyStageId] ?? humanize(id);

export const touchpointLabel = (id: string): string =>
  TOUCHPOINT_LABELS[id] ?? humanize(id);

export const quarantineReasonLabel = (reason: string): string =>
  QUARANTINE_REASON_LABELS[reason as MemberJourneyQuarantineReason] ??
  humanize(reason);

export const resultModalityLabel = (modality: string): string =>
  RESULT_MODALITY_LABELS[modality as MemberJourneyResultModality] ??
  humanize(modality);

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  quest: 'Quest',
  'quest-lab': 'Quest results',
  'boston-heart': 'Boston Heart',
  'other-labs': 'Other labs',
  'full-body-mri': 'Full body MRI',
  'brain-mri': 'Brain MRI',
  'mri-other': 'MRI',
  ccta: 'CCTA',
  cleerly: 'Cleerly CCTA',
  'ct-calcium-score': 'CT calcium score',
  dexa: 'DEXA',
  'vo2-max': 'VO2 max',
};

export const documentTypeLabel = (code: string): string =>
  DOCUMENT_TYPE_LABELS[code] ?? humanize(code.replace(/-/g, '_'));

const PROVENANCE_LABELS: Record<MemberJourneyResultProvenance, string> = {
  charted: 'Charted in Elation',
  imaging_system: 'From imaging system',
  extracted: 'Extracted from a document',
};

const RESULT_SET_STATUS_LABELS: Record<string, string> = {
  completed: 'complete',
  pending: 'awaiting results',
  expired: 'expired',
};

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** One readable line per piece of evidence: what it is, and when. */
export type EvidenceSummary = {
  title: string;
  details: string[];
  date?: string;
};

export const describeEvidence = (
  evidence: MemberJourneyEvidence,
): EvidenceSummary => {
  switch (evidence.kind) {
    case 'fhir_composition':
      return {
        title: evidence.title ?? 'Lab result set',
        details: [
          `${evidence.received} of ${plural(
            evidence.expected,
            'test',
          )} received`,
          ...(evidence.status
            ? [RESULT_SET_STATUS_LABELS[evidence.status] ?? evidence.status]
            : []),
        ],
        date: evidence.startDate,
      };
    case 'fhir_diagnostic_report':
      return {
        title:
          evidence.label ??
          (evidence.documentType
            ? documentTypeLabel(evidence.documentType)
            : 'Lab report'),
        details: [
          PROVENANCE_LABELS[evidence.provenance],
          ...(evidence.resultCount
            ? [plural(evidence.resultCount, 'result')]
            : []),
        ],
        date: evidence.effective,
      };
    case 'fhir_document_reference':
      return {
        title:
          evidence.label ??
          (evidence.documentType
            ? documentTypeLabel(evidence.documentType)
            : 'Document'),
        details: [PROVENANCE_LABELS[evidence.provenance]],
        date: evidence.created,
      };
    case 'fhir_observation':
      return {
        title: evidence.label ?? 'Extracted value',
        details: [
          PROVENANCE_LABELS[evidence.provenance],
          evidence.reviewed ? 'reviewed' : 'not reviewed',
        ],
        date: evidence.effective,
      };
    case 'fhir_procedure_request':
      if (evidence.category === 'imaging') {
        return {
          title: 'Imaging order',
          details: [
            ...(evidence.label ? [evidence.label] : []),
            plural(evidence.testCount, 'test'),
          ],
          date: evidence.authoredOn,
        };
      }
      return {
        title: evidence.vendor === 'quest' ? 'Quest lab order' : 'Lab order',
        details: [
          plural(evidence.testCount, 'test'),
          ...(evidence.inHouse ? ['collected in house'] : []),
          ...(evidence.status === 'completed' ? ['fulfilled'] : []),
        ],
        date: evidence.authoredOn,
      };
    case 'fhir_appointment':
      return {
        title: 'Appointment',
        details: evidence.status ? [evidence.status] : [],
        date: evidence.start,
      };
    case 'fhir_consent':
      return { title: 'Consent signed', details: [], date: evidence.dateTime };
    case 'fhir_questionnaire_response':
      return {
        title: 'Survey submitted',
        details: [],
        date: evidence.authored,
      };
    case 'salesforce_task':
      return {
        title: evidence.subject,
        details: [
          'Salesforce task',
          ...(evidence.status ? [evidence.status] : []),
        ],
        date: evidence.completedAt,
      };
    case 'salesforce_stage':
      return { title: evidence.stage, details: ['Salesforce stage'] };
  }
};

/** When labs were ordered for a result that has not come back yet. */
export const orderedAtFor = (
  tp: MemberJourneyTouchpoint,
): string | undefined => {
  if (tp.complete) return undefined;
  return tp.evidence
    .map(e =>
      e.kind === 'fhir_procedure_request'
        ? e.authoredOn
        : e.kind === 'fhir_composition' && e.status === 'pending'
        ? e.startDate
        : undefined,
    )
    .filter((day): day is string => Boolean(day))
    .sort()
    .pop();
};

export const LAB_STEPS = [
  {
    id: 'dod',
    label: 'Day of diagnostics',
    stageId: 'day_of_diagnostics',
    touchpointId: 'day_of_diagnostics',
    itemId: 'dod_labs_results',
  },
  {
    id: '4mo',
    label: '4 month labs',
    stageId: 'care_period_1',
    touchpointId: 'physician_4mo_lab_ror',
    itemId: 'labs_4mo_results',
  },
  {
    id: '8mo',
    label: '8 month labs',
    stageId: 'care_period_2',
    touchpointId: 'physician_8mo_lab_ror',
    itemId: 'labs_8mo_results',
  },
] as const;

export type LabStep = {
  id: (typeof LAB_STEPS)[number]['id'];
  label: string;
  stageId: MemberJourneyStageId;
  touchpointId: string;
  item: MemberJourneyTouchpoint;
};

/** The three lab result items, in journey order; absent on old projections. */
export const labStepsFor = (projection: MemberJourneyProjection): LabStep[] =>
  LAB_STEPS.flatMap(step => {
    const item = projection.stages
      .find(s => s.id === step.stageId)
      ?.touchpoints.find(tp => tp.id === step.touchpointId)
      ?.items?.find(i => i.id === step.itemId);
    return item
      ? [
          {
            id: step.id,
            label: step.label,
            stageId: step.stageId,
            touchpointId: step.touchpointId,
            item,
          },
        ]
      : [];
  });

/**
 * Operator-facing classification of a touchpoint. Derived here from the raw
 * projection fields and the clock; the projector's `signal` is ignored so
 * that classification rules live in one place.
 */
export type SignalTone =
  | 'confirmed'
  | 'weak'
  | 'scheduled'
  | 'missing'
  | 'pending'
  | 'not_tracked';

const DAY_MS = 24 * 60 * 60 * 1000;

// expectedAt is a UTC calendar day (YYYY-MM-DD); a touchpoint is not late
// until that whole day has passed.
const dueEndMs = (expectedAt: string): number =>
  Date.parse(`${expectedAt.slice(0, 10)}T00:00:00.000Z`) + DAY_MS;

export const toneFor = (
  tp: MemberJourneyTouchpoint,
  now: Date = new Date(),
): SignalTone => {
  if (tp.complete) {
    return tp.confidence === 'confirmed' ? 'confirmed' : 'weak';
  }
  // No signal says this result is expected for the member, so its absence is
  // not a review signal.
  if (tp.expectedBy?.length === 0) return 'not_tracked';
  if (tp.scheduledAt) return 'scheduled';
  if (!tp.expectedAt || dueEndMs(tp.expectedAt) > now.getTime()) {
    return 'pending';
  }
  return 'missing';
};

export type StageSummary = {
  id: MemberJourneyStageId;
  label: string;
  complete: number;
  total: number;
  reviewSignals: number;
  weak: number;
  scheduled: number;
  tone: 'confirmed' | 'mixed' | 'pending';
  hasAlert: boolean;
};

export type JourneyReport = {
  stages: StageSummary[];
  totals: { reviewSignals: number; weak: number; scheduled: number };
  defaultStageId: MemberJourneyStageId | null;
  defaultTouchpointId: string | null;
};

const countTone = (
  touchpoints: MemberJourneyTouchpoint[],
  tone: SignalTone,
  now: Date,
): number => touchpoints.filter(tp => toneFor(tp, now) === tone).length;

/** First overdue touchpoint in a stage, else its first touchpoint. */
export const defaultTouchpointFor = (
  touchpoints: MemberJourneyTouchpoint[],
  now: Date = new Date(),
): MemberJourneyTouchpoint | undefined =>
  touchpoints.find(tp => toneFor(tp, now) === 'missing') ?? touchpoints[0];

/**
 * Tallies each stage and picks the default selection: the first stage with a
 * review signal (else first incomplete, else first) and its first overdue
 * touchpoint. Nested `items` (intake consents/surveys) count only through
 * their parent.
 */
export const deriveJourneyReport = (
  projection: MemberJourneyProjection,
  now: Date = new Date(),
): JourneyReport => {
  const stages = projection.stages.map<StageSummary>(stage => {
    const complete = stage.touchpoints.filter(tp => tp.complete).length;
    const total = stage.touchpoints.length;
    const reviewSignals = countTone(stage.touchpoints, 'missing', now);
    const weak = countTone(stage.touchpoints, 'weak', now);
    const scheduled = countTone(stage.touchpoints, 'scheduled', now);
    return {
      id: stage.id,
      label: stageLabel(stage.id),
      complete,
      total,
      reviewSignals,
      weak,
      scheduled,
      tone:
        total > 0 && complete === total
          ? 'confirmed'
          : complete > 0 || weak > 0
          ? 'mixed'
          : 'pending',
      hasAlert: reviewSignals > 0,
    };
  });

  const defaultStage =
    stages.find(s => s.hasAlert) ??
    stages.find(s => s.complete < s.total) ??
    stages[0];
  const defaultStageTouchpoints =
    projection.stages.find(s => s.id === defaultStage?.id)?.touchpoints ?? [];
  const defaultTouchpoint = defaultTouchpointFor(defaultStageTouchpoints, now);

  return {
    stages,
    totals: {
      reviewSignals: stages.reduce((n, s) => n + s.reviewSignals, 0),
      weak: stages.reduce((n, s) => n + s.weak, 0),
      scheduled: stages.reduce((n, s) => n + s.scheduled, 0),
    },
    defaultStageId: defaultStage?.id ?? null,
    defaultTouchpointId: defaultTouchpoint?.id ?? null,
  };
};

export type TouchpointStatus =
  | { kind: 'confirmed'; occurredAt?: string }
  | { kind: 'inferred'; occurredAt?: string; viaStage: boolean }
  | { kind: 'scheduled'; scheduledAt?: string }
  | {
      kind: 'overdue';
      expectedAt: string;
      daysOverdue: number;
      orderedAt?: string;
    }
  | { kind: 'missing'; orderedAt?: string }
  | { kind: 'expected'; expectedAt?: string; orderedAt?: string }
  | { kind: 'not_tracked' };

export type ReviewReason =
  | { kind: 'task_open'; status?: string }
  | { kind: 'no_evidence' };

/** Status line for a touchpoint dot/badge. */
export const describeTouchpoint = (
  tp: MemberJourneyTouchpoint,
  now: Date = new Date(),
): TouchpointStatus => {
  const orderedAt = orderedAtFor(tp);
  const ordered = orderedAt ? { orderedAt } : {};
  switch (toneFor(tp, now)) {
    case 'confirmed':
      return { kind: 'confirmed', occurredAt: tp.occurredAt };
    case 'weak':
      return {
        kind: 'inferred',
        occurredAt: tp.occurredAt,
        viaStage: tp.confidence === 'stage_fallback',
      };
    case 'scheduled':
      return { kind: 'scheduled', scheduledAt: tp.scheduledAt };
    case 'pending':
      return { kind: 'expected', expectedAt: tp.expectedAt, ...ordered };
    case 'not_tracked':
      return { kind: 'not_tracked' };
    case 'missing':
      return tp.expectedAt
        ? {
            kind: 'overdue',
            expectedAt: tp.expectedAt,
            daysOverdue: Math.max(
              1,
              Math.floor(
                (now.getTime() - new Date(tp.expectedAt).getTime()) / DAY_MS,
              ),
            ),
            ...ordered,
          }
        : { kind: 'missing', ...ordered };
  }
};

/** Why an overdue touchpoint needs review; `null` for any other tone. */
export const reviewReasonFor = (
  tp: MemberJourneyTouchpoint,
  now: Date = new Date(),
): ReviewReason | null => {
  if (toneFor(tp, now) !== 'missing') return null;
  const task = tp.evidence.find(
    (e): e is Extract<MemberJourneyEvidence, { kind: 'salesforce_task' }> =>
      e.kind === 'salesforce_task',
  );
  return task
    ? { kind: 'task_open', status: task.status }
    : { kind: 'no_evidence' };
};
